import React, { useContext } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { sortBy } from 'lodash';

import AddButton from 'components/action/AddButton';
import IndicatorEditionModal from 'components/Modal/IndicatorEditionModal';

import { ConfirmModal } from 'components/Modal/ConfirmModal';

import {
	getItemAlteredByScenario,
	vulnerabilityIndicators
} from 'utils/rules';

import UIContext from 'components/Utils/UIContext';
import { SideMenu } from 'layout/SideMenu';
import { ColumnsDisplayTab } from 'components/tabs/ColumnsDisplayTab';

import { useIndicatorController } from 'hooks/useIndicatorController';
import { useStakeholderController } from 'hooks/useStakeholderController';
import { StakeholderImpactsTab } from 'components/tabs/StakeholderImpactsTab';
import { FishboneLinksTab } from 'components/tabs/FishboneLinksTab';
import dayjs from 'dayjs';
import { MaskableComponent } from 'components/Tutorial/MaskableComponent';


import { FiltersTab } from './Tabs/FiltersTab';
import { CommentsTab } from '../Navigator/Tabs/CommentsTab';
import BotTab from 'components/tabs/BotTab';
import DisabledTab from 'components/tabs/DisabledTab';
import { IndicatorGeneralTab } from './Tabs/IndicatorGeneralTab';
import { IndicatorsList } from './IndicatorsList';
import { IndicatorModel } from 'model/IndicatorModel';
import { Flex, Layout, Tabs, Tag } from 'antd';
import Nav from '../Scenarios/Nav';
import { IndicatorsBSC } from './IndicatorsBSC';
import { ExportTab } from './Tabs/ExportTab';
import { ItemTagsTab } from 'components/tabs/ItemTagsTab';

import './Indicators.sass';

const indicatorsLimit = 25;
const selectableElementTypes = ['Indicator']

type FilterObject = {
	name: string,
	categoryId: string[],
	frequency: string[],
	tagFilter: string[],
}

const filterIndicators = (indicators: IndicatorModel[], filters: FilterObject) => {

	let limitedIndicators = indicators.map((r, index) => ({ ...r, index }))

	if (filters && filters.name) {
		limitedIndicators = limitedIndicators.filter(r => {
			return r.name.toLowerCase().includes(filters.name.toLowerCase())
		})
	}

	if (filters && filters.categoryId?.length > 0) {
		limitedIndicators = limitedIndicators.filter(r => {
			const catId = (r as any).indicatorCategoryId;
			const matchesNone = filters.categoryId.includes('__none__') && !catId;
			const matchesCat = filters.categoryId.some((id) => id !== '__none__' && id === catId);
			return matchesNone || matchesCat;
		})
	}

	if (filters && filters.frequency?.length > 0) {
		limitedIndicators = limitedIndicators.filter(r => {
			const freq = (r as any).frequency;
			const matchesNone = filters.frequency.includes('__none__') && !freq;
			const matchesFreq = filters.frequency.some((f) => f !== '__none__' && f === freq);
			return matchesNone || matchesFreq;
		})
	}

	if (filters && filters.tagFilter?.length > 0) {
		limitedIndicators = limitedIndicators.filter(r =>
			((r as any).taggings || []).some((t: any) => filters.tagFilter.includes(t.tagId))
		)
	}

	return limitedIndicators
}

export const Indicators = (props: any) => {
	const [editedItem, setEditedItem] = React.useState<any | null>(null);
	const [categoryFilter, setCategoryFilter] = React.useState<string[]>([]);
	const [frequencyFilter, setFrequencyFilter] = React.useState<string[]>([]);
	const [tagFilter, setTagFilter] = React.useState<string[]>([]);
	// Onglet actif : liste ('indicators') ou visualisation BSC ('summary').
	const [activeTab, setActiveTab] = React.useState<string>('indicators');
	const params: any = useParams();

	const { createIndicator, updateIndicator, updateIndicatorTaggings, deleteIndicator } = useIndicatorController();
	const {
		createIndicatorImpact,
		deleteIndicatorImpact,
		updateIndicatorImpactStrength
	} = useStakeholderController();

	const { t } = useTranslation();

	const { indicators = [] } = props;

	const selection = props.selection?.__typename === "Indicator" ? indicators.find((r: any) => r.id === props.selection?.id) : null;

	let alteredIndicators = sortBy(
		indicators.map((c, index) => ({
			...getItemAlteredByScenario(c, props.scenario),
			index: index,
			key: c.id || `indicator-${index}` // Ajout d'une clé unique pour chaque élément
		})
		).filter(c => c.enabled !== false),
		'name'
	);

	const frequencies = [...new Set(indicators.map((i: any) => i.frequency).filter(Boolean))] as string[];

	const allTags = sortBy(props.tags || [], 'name');
	const usedTags = [...new Set((alteredIndicators as any[]).flatMap(i => (i.taggings || []).map((t: any) => t.tagId)))];

	const getAggregatedFilters = (): FilterObject => {
		return {
			name: props.searchTerm,
			categoryId: categoryFilter,
			frequency: frequencyFilter,
			tagFilter,
		}
	}

	const filteredIndicators = filterIndicators(alteredIndicators, getAggregatedFilters())

	let vulnerabilities = (props.scenarios || []).map((sc) => {
		return {
			id: sc.id,
			name: sc.name,
			vulnerability: vulnerabilityIndicators(
				indicators.map((c) => getItemAlteredByScenario(c, sc))
			)
		};
	});

	vulnerabilities.push({
		id: null,
		name: t('models.scenario.baseline'),
		vulnerability: vulnerabilityIndicators(indicators)
	});

	// Create
	const handleCreateIndicator = async (indicatorCategoryId: string | null = null) => {
		props.pushModal(
			<IndicatorEditionModal
				onHide={() => {
					props.popModal();
				}}
				onSave={async (values: any) => {
					let variables = {
						projectId: params.id,
						name: values.name,
						weight: values.weight || 1,
						trend: '50',
						description: values.description || '',
						reference: values.reference || '',
						indicatorCategoryId: values.indicatorCategoryId || null,
						evaluatedAt: values.evaluatedAt || '',
						frequency: values.frequency || null,
					};

					let result = await createIndicator(params.id, variables, {}, props.scenario, props.scenarios || []);

					props.popModal();
				}}
				id={false}
				indicatorCategories={props.indicatorCategories || []}
				frequencies={frequencies}
				values={{
					name: '',
					weight: 1,
					description: '',
					reference: '',
					indicatorCategoryId: indicatorCategoryId || null,
					evaluatedAt: null,
				}}
			></IndicatorEditionModal>
		);
	};


	// Update
	const handleUpdateIndicator = async (id: string, values: Object) => {
		updateIndicator(id, indicators || [], props.scenario, values);
	};

	const askDeleteConfirmation = async (id?: string) => {
		const confirmProps = {
			title: t('models.indicator.messages.delete_confirmation'),
			message: t('models.indicator.messages.delete_confirmation_explanation'),
			onConfirm: () => deleteIndicator(params.id, id),
			onHide: props.popModal,
			t
		};

		props.pushModal(
			<ConfirmModal {...confirmProps} />
		);
	};

	const uiContext = useContext(UIContext);

	return (
		<Layout className="Indicators w-100" data-testid="indicators-page">

			<Layout.Content className="Body pt-1">
				<MaskableComponent masked={uiContext.maskedZones['main']}>

					<Tabs
						type="card"
						activeKey={activeTab}
						onChange={setActiveTab}
						items={[
							{
								key: 'indicators',
								label: t('pages.indicators.title'),
								children: (
									<div className="content pb-3 d-flex flex-column align-items-center" data-testid="indicators-content">
										<div className="pr-1 w-100">
											<IndicatorsList
												indicators={filteredIndicators}
												indicatorCategories={props.indicatorCategories || []}
												scenarios={props.scenarios}
												scenario={props.scenario}
												onUpdateIndicator={handleUpdateIndicator}
												onEditIndicator={setEditedItem}
												showCategory={uiContext.preferences?.pages?.indicators?.category !== false}
												selection={selection}
												deselect={props.deselect}
												select={props.select}
												readOnly={!uiContext.canEdit}
												showDescription={uiContext.preferences?.pages?.indicators?.description !== false}
											/>

										</div>

										<Flex justify="center" align="center">
											{uiContext.canEdit && indicators?.length < indicatorsLimit && (
												<AddButton
													id="add-indicator"
													data-testid="add-indicator-button"
													onClick={() => handleCreateIndicator(null)}
												/>
											)}
										</Flex>

									</div>
								)
							},
							{
								key: 'summary',
								label: t('pages.risks.visualization_tab'),
								children: (
									<div style={{ width: '100%' }} data-testid="indicators-visualization">
										<div style={{ maxWidth: 900, margin: '0 auto', width: '100%' }}>
											<Nav
												className="mb-2 w-100"
												fullWidth
												onGoToPreviousScenario={props.onGoToPreviousScenario}
												onGoToNextScenario={props.onGoToNextScenario}
											>
												<div
													className="text-center"
													style={{
														height: '3.4rem',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center'
													}}
												>
													<strong style={{ fontSize: '1.1rem' }}>
														{props.scenario?.name || t('models.scenario.baseline')}
													</strong>
												</div>
											</Nav>
										</div>
										<div className="pr-1 pl-1">
											<IndicatorsBSC
												indicators={indicators}
												indicatorCategories={props.indicatorCategories || []}
												scenario={props.scenario}
												scenarios={props.scenarios}
												select={props.select}
												deselect={props.deselect}
												selection={selection}
												onEdit={uiContext.canEdit ? setEditedItem : undefined}
												onCreate={uiContext.canEdit ? (categoryId) => handleCreateIndicator(categoryId) : null}
											onUpdateIndicator={uiContext.canEdit ? handleUpdateIndicator : undefined}
											/>
										</div>
									</div>
								)
							}
						]}
					/>
				</MaskableComponent>
			</Layout.Content>

			<SideMenu
				defaultTab={props.selection && selection?.__typename && selectableElementTypes.includes(selection?.__typename) ? "general" : "filters"}
				tabs={(() => {
					// Calcul du nombre d'éléments désactivés
					const disabledCount = indicators.filter(c => getItemAlteredByScenario(c, props.scenario).enabled === false).length;

					return [
						(selection?.__typename && selectableElementTypes.includes(selection?.__typename)) && {
							id: 'general',
							title: t('pages.indicators.tabs.general.title'),
						},
						!selection && {
							id: 'filters',
							title: t('pages.indicators.tabs.filters.title')
						},
						!selection && {
							id: 'display',
							title: t('pages.risks.tabs.options.title')
						},
						selection?.__typename === 'Indicator' && props.features?.fishbone && {
							id: 'fishbone',
							title: t('pages.fishbone.title')
						},
						selection?.__typename === 'Indicator' && {
							id: 'stakeholders',
							title: t('models.item.name_plural')
						},
						selection?.__typename === 'Indicator' && {
							id: 'tags',
							title: t('pages.navigator.tabs.tags.title')
						},
						selection?.__typename && selectableElementTypes.includes(selection?.__typename) && {
							id: 'comments',
							title: t('pages.indicators.tabs.comments.title')
						},
						!selection && {
							id: 'deactivated',
							title: `${t('pages.navigator.tabs.disabled.title')}${disabledCount > 0 ? ` (${disabledCount})` : ''}`
						},
						!selection && {
							id: 'bot',
							title: t('pages.indicators.tabs.bot.title')
						},
						!selection && {
							id: 'export',
							title: t('pages.navigator.tabs.export.title')
						},
					];
				})()}>
				{function (selectedTab, setSelectedTab: (t: string) => void) {
					return <>{selectedTab == 'comments' && (
						<CommentsTab
							projectId={params.id}
							key={props.selection.id}
							type={props.selection?.__typename}
							selection={props.selection}
							pushModal={props.pushModal}
							popModal={props.popModal}
							scenario={props.scenario}
						></CommentsTab>
					)}

						{selectedTab == 'tags' && selection?.__typename === 'Indicator' && (
							<ItemTagsTab
								allTags={props.tags || []}
								item={selection}
								onClose={() => props.deselect()}
								onUpdateTags={async (item, tagIds) => {
									await updateIndicatorTaggings(item, tagIds);
									const kept = (item.taggings || []).filter((t: any) => tagIds.includes(t.tagId));
									const added = tagIds.filter((id: string) => !(item.taggings || []).find((t: any) => t.tagId === id)).map((id: string) => ({ tagId: id, objectId: item.id }));
									props.select({ ...item, taggings: [...kept, ...added] });
								}}
								onCreateTag={props.onCreateTag}
								areTagsVisible={props.preferences?.pages?.navigator?.tags ?? true}
								setPreferences={props.setPreferences}
								scenario={props.scenario}
							/>
						)}

						{selectedTab == 'general' && selection?.__typename === "Indicator" && (
							<>
								<IndicatorGeneralTab
									selection={selection}
									scenarios={props.scenarios}
									onUpdateTrend={(val) => handleUpdateIndicator(selection?.id, { trend: val })}
									risks={props.risks}
									stakes={props.stakes}
									fishboneBranchs={props.fishboneBranchs}

									onEdit={() => {
										setEditedItem(selection)
									}}
									onDisable={async () => {
										await updateIndicator(selection?.id, indicators, props.scenario, { enabled: false });
										props.deselect();
									}}
									onDelete={askDeleteConfirmation}

									scenario={props.scenario}

									onClose={() => {
										props.deselect()
									}}
								></IndicatorGeneralTab>
								<div className="px-1 mt-1" onClick={() => setSelectedTab('tags')} style={{ cursor: 'pointer' }}>
									{(selection?.taggings?.length > 0) ? (
										(selection.taggings as any[]).map((tagging: any) => {
											const tag = allTags.find((t: any) => t.id === tagging.tagId);
											return tag ? (
												<Tag.CheckableTag key={tag.id} checked className="noselection">
													{tag.name}
												</Tag.CheckableTag>
											) : null;
										})
									) : (
										<span style={{ color: 'var(--ant-color-text-tertiary)', fontSize: '0.85rem' }}>Tags →</span>
									)}
								</div>
							</>
						)}

						{selectedTab === 'stakeholders' && selection?.__typename === 'Indicator' && (
							<StakeholderImpactsTab
								selection={selection}
								stakeholders={props.stakeholders || []}
								impactedFieldName="impactedIndicators"
								targetIdFieldName="indicatorId"
								entityTypeLabel={t('models.indicator.name')}
								createImpact={async (stakeholderId, indicatorId) => {
									await createIndicatorImpact(stakeholderId, indicatorId);
									props.select({ ...selection });
								}}
								updateImpactStrength={async (impact, strength) => {
									await updateIndicatorImpactStrength(impact, strength);
									props.select({ ...selection });
								}}
								deleteImpact={async (id, stakeholderId) => {
									await deleteIndicatorImpact(id, stakeholderId);
									props.select({ ...selection });
								}}
								onClose={() => props.deselect()}
							/>
						)}

						{selectedTab === 'fishbone' && selection?.__typename === 'Indicator' && (
							<FishboneLinksTab
								selection={selection}
								targetType="indicator"
								fishboneBranchs={props.fishboneBranchs}
								scenario={props.scenario}
								select={props.select}
								onClose={() => props.deselect()}
							/>
						)}

						{selectedTab == 'filters' && (
							<FiltersTab
								setSearchTerm={props.setSearchTerm}
								searchTerm={props.searchTerm}
								categories={props.indicatorCategories || []}
								categoryFilter={categoryFilter}
								setCategoryFilter={setCategoryFilter}
								frequencies={frequencies}
								frequencyFilter={frequencyFilter}
								setFrequencyFilter={setFrequencyFilter}
								selection={selection}
								deselect={props.deselect}
								select={props.select}
								onEdit={() => {
									setEditedItem(selection)
								}}
								tags={allTags}
								usedTags={usedTags}
								tagFilter={tagFilter}
								setTagFilter={setTagFilter}
							></FiltersTab>
						)}

						{selectedTab == 'display' && (
							<ColumnsDisplayTab
								displayColumns={activeTab !== 'summary'}
								showCategoryToggle={(props.indicatorCategories || []).length > 0}
								preferences={uiContext.preferences?.pages?.indicators}
								setPreferences={(key, value) =>
									uiContext.setPreferences('pages', 'indicators.' + key, value)
								}
							/>
						)}

						{selectedTab == 'bot' && (
							<BotTab
								projectId={params.id}
								messages={props.botThread?.messages || []}
								onResetThread={props.resetBotThread}
								onSendMessage={(message, callback) => props.addBotMessage(params.id, props.scenario?.id || null, "indicators", message, callback)}

							></BotTab>
						)}

						{selectedTab == 'deactivated' && (
							<DisabledTab
								scenario={props.scenario}
								indicators={props.indicators}
								select={props.select}
								onEnableItem={async (item) => {
									if (item.__typename === 'Indicator') {
										await updateIndicator(item.id, indicators, props.scenario, { enabled: true });
									}
								}}
								onDeleteItem={(item) => {
									if (item.__typename === 'Indicator') {
										askDeleteConfirmation(item.id);
									}
								}}
							/>
						)}

						{selectedTab == 'export' && (
							<ExportTab
								indicators={filteredIndicators}
								indicatorCategories={props.indicatorCategories || []}
							/>
						)}
					</>
				}}
			</SideMenu>

			{editedItem && (() => {
				// Appliquer les valeurs du scénario si un scénario est actif
				const alteredEditedItem = getItemAlteredByScenario(editedItem, props.scenario);
				return (
				<IndicatorEditionModal
					onHide={() => {
						setEditedItem(null);
					}}
					onDelete={() => {
						askDeleteConfirmation(editedItem.id);
						setEditedItem(null);
					}}
					onSave={async (values: any) => {
						await handleUpdateIndicator(editedItem.id, values);
						if (values.taggings !== undefined)
							await updateIndicatorTaggings(editedItem, values.taggings);
						setEditedItem(null);
					}}
					id={editedItem?.id}
					indicatorCategories={props.indicatorCategories || []}
					frequencies={frequencies}
					tags={props.tags || []}
					values={{
						// Champs baseline (non modifiables par scénario)
						name: editedItem?.name,
						description: editedItem?.description || '',
						reference: editedItem?.reference,
						indicatorCategoryId: editedItem?.indicatorCategoryId || null,
						evaluatedAt: editedItem?.evaluatedAt ? dayjs(editedItem?.evaluatedAt) : null,
						frequency: editedItem?.frequency || '',
						taggings: editedItem?.taggings?.map((t: any) => t.tagId) || [],
						// Champs modifiables par scénario - utiliser les valeurs altérées
						weight: alteredEditedItem?.weight || 1,
					}}
				/>
				);
			})()}
		</Layout>
	);
};
